import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useCms } from '@/context/CmsContext';
import styles from './cms.module.css';

interface EditableProps {
    page: string;
    section: string;
    field: string;
    value: string;
    fieldType?: 'text' | 'url' | 'image' | 'video' | 'markdown';
    children: React.ReactNode;
}

export default function Editable({ page, section, field, value, fieldType = 'text', children }: EditableProps) {
    const { isAdmin, isEditMode, trackChange, getPendingValue } = useCms();
    const [editing, setEditing] = useState(false);
    const editRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const liveValueRef = useRef(value);

    const pendingValue = getPendingValue(page, section, field);
    const displayValue = pendingValue !== undefined ? pendingValue : value;
    const hasChange = pendingValue !== undefined;

    useEffect(() => {
        liveValueRef.current = displayValue;
    }, [displayValue]);

    // Focus and place cursor when entering edit mode
    useEffect(() => {
        if (!editing) return;
        const el = editRef.current;
        if (el) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
            el.focus();
        }
        inputRef.current?.focus();
    }, [editing]);

    const commitEdit = useCallback(() => {
        const newValue = (fieldType === 'url' || fieldType === 'video')
            ? (inputRef.current?.value ?? displayValue)
            : liveValueRef.current;

        if (newValue !== value) {
            trackChange(page, section, field, newValue, value, fieldType);
        }
        setEditing(false);
    }, [value, displayValue, page, section, field, fieldType, trackChange]);

    const cancelEdit = useCallback(() => {
        liveValueRef.current = displayValue;
        if (editRef.current) editRef.current.textContent = displayValue;
        setEditing(false);
    }, [displayValue]);

    if (!isAdmin || !isEditMode) {
        // Even when not editing, show pending value if it exists (after a save cycle)
        return <>{children}</>;
    }

    if (editing) {
        if (fieldType === 'text' || fieldType === 'markdown') {
            return (
                <div
                    ref={editRef}
                    className={styles.editing}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={() => {
                        liveValueRef.current = editRef.current?.textContent || '';
                    }}
                    onBlur={commitEdit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitEdit(); }
                        if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                    }}
                    dangerouslySetInnerHTML={{ __html: displayValue }}
                />
            );
        }

        return (
            <div className={styles.editOverlay}>
                <label className={styles.editLabel}>{fieldType}: {field}</label>
                <input
                    ref={inputRef}
                    className={styles.editInput}
                    type="text"
                    defaultValue={displayValue}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') commitEdit();
                        if (e.key === 'Escape') cancelEdit();
                    }}
                    onBlur={commitEdit}
                    autoFocus
                />
            </div>
        );
    }

    // Not editing — show pending value by cloning children with new text
    const wrapperClass = hasChange
        ? `${styles.editable} ${styles.editableChanged}`
        : styles.editable;

    const renderedChildren = hasChange
        ? React.Children.map(children, child => {
            if (React.isValidElement(child)) {
                // Clone the element and replace its children with the pending text
                return React.cloneElement(child as React.ReactElement<any>, {}, displayValue);
            }
            return displayValue;
        })
        : children;

    return (
        <div
            className={wrapperClass}
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setEditing(true);
            }}
            title={`Click to edit: ${field}`}
        >
            {renderedChildren}
        </div>
    );
}
